import React, { useCallback, useEffect, useState } from "react";
import { Download, Search, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import SubmissionsTable from "../../components/admin/SubmissionsTable";
import SubmissionDetail from "../../components/admin/SubmissionDetail";
import { errorMessage, submissionsApi } from "../../lib/api";
import { FORM_TYPES, STATUS_OPTIONS } from "../../lib/forms";
import { cn } from "../../lib/utils";

const Chip = ({ active, onClick, children, count, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex items-center gap-2 border px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors",
      active ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground/80 hover:border-foreground/50"
    )}
    data-testid={testId}
  >
    {children}
    {count !== undefined && <span className={cn("text-[10px]", active ? "text-primary-foreground/80" : "text-muted-foreground")}>{count}</span>}
  </button>
);

const CRUMBS = [{ label: "Submissions" }];

const Submissions = () => {
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState(null);
  const [formType, setFormType] = useState("");
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await submissionsApi.list({ form_type: formType || undefined, status: status || undefined, q: query.trim() || undefined });
      setItems(data.items);
      setCounts(data.counts);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load submissions"));
      setItems([]);
    }
  }, [formType, status, query]);

  useEffect(() => {
    const t = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, query]);

  const onChanged = (doc) => setItems((prev) => (prev ? prev.map((s) => (s.id === doc.id ? { ...s, ...doc } : s)) : prev));
  const onDeleted = (id) => {
    setOpenId(null);
    setItems((prev) => (prev ? prev.filter((s) => s.id !== id) : prev));
    load();
  };
  const closeDetail = () => {
    setOpenId(null);
    load(); // refresh counts (new -> read, status changes)
  };

  return (
    <div className="space-y-6" data-testid="submissions-page">
      <AdminPageHeader
        crumbs={CRUMBS}
        eyebrow="Inbox"
        title="Submissions"
        description="Everything visitors send through your forms. Open one to see the details, who was emailed, add notes or forward it to a teammate."
        actions={
          <>
            <Button variant="outline" className="h-11 rounded-none px-5 text-[11px] font-semibold uppercase tracking-[0.2em]" onClick={load} data-testid="submissions-refresh">
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button asChild variant="outline" className="h-11 rounded-none px-5 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="submissions-export">
              <a href={submissionsApi.exportUrl(formType)} download>
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </a>
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="submissions-form-filters">
          <Chip active={!formType} onClick={() => setFormType("")} count={counts?.total} testId="filter-form-all">
            All forms
          </Chip>
          {FORM_TYPES.map((f) => (
            <Chip key={f.id} active={formType === f.id} onClick={() => setFormType(f.id)} count={counts?.by_form?.[f.id]} testId={`filter-form-${f.id}`}>
              {f.label}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-2" data-testid="submissions-status-filters">
            <Chip active={!status} onClick={() => setStatus("")} testId="filter-status-all">
              Any status
            </Chip>
            {STATUS_OPTIONS.map((o) => (
              <Chip key={o.id} active={status === o.id} onClick={() => setStatus(o.id)} count={counts?.by_status?.[o.id]} testId={`filter-status-${o.id}`}>
                {o.label}
              </Chip>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, message" className="h-10 rounded-none border-border bg-transparent pl-9 pr-9 text-sm" data-testid="submissions-search" />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" aria-label="Clear search">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <SubmissionsTable items={items} onOpen={setOpenId} emptyTitle={counts?.total ? "Nothing matches" : "No submissions yet"} />

      <SubmissionDetail id={openId} onClose={closeDetail} onChanged={onChanged} onDeleted={onDeleted} />
    </div>
  );
};

export default Submissions;
